"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { HouseIcon, MessagesSquareIcon, MilestoneIcon } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * La barre de navigation de l'espace client — fixée en bas, sous le pouce.
 *
 * C'est ce qui fait passer Cairn d'une page qui défile à un *vrai outil* : trois
 * lieux, « Aujourd'hui » (l'instant, ce qu'il y a à faire), « Le fil » (la voix,
 * l'échange avec le coach) et « Parcours » (la durée, le chemin tenu). Sobre,
 * dans la palette : capitales condensées, angles quasi droits, thème sombre.
 * Aucun badge, aucun compteur clignotant.
 */
const ITEMS = [
  { href: "/mon-espace", label: "Aujourd'hui", icon: HouseIcon },
  { href: "/mon-espace/fil", label: "Le fil", icon: MessagesSquareIcon },
  { href: "/mon-espace/parcours", label: "Parcours", icon: MilestoneIcon },
] as const

export function EspaceNav() {
  const pathname = usePathname()

  // La séance vécue est un plein écran focalisé : elle porte sa propre sortie
  // (« Quitter ») et ses commandes en bas. La barre de navigation viendrait
  // concurrencer ce focus — on l'efface le temps de la traversée.
  if (pathname.startsWith("/mon-espace/seance")) return null

  return (
    <nav
      aria-label="Espace client"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm"
    >
      <ul className="mx-auto flex max-w-[560px] items-stretch">
        {ITEMS.map((item) => {
          // « Aujourd'hui » est la racine : match exact, sinon il resterait
          // actif sur /parcours dont il est le préfixe.
          const actif =
            item.href === "/mon-espace"
              ? pathname === "/mon-espace"
              : pathname.startsWith(item.href)
          const Icone = item.icon

          return (
            <li key={item.href} className="flex-1">
              <Link
                href={item.href}
                aria-current={actif ? "page" : undefined}
                className={cn(
                  "flex flex-col items-center gap-1.5 py-3.5 outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50",
                  actif
                    ? "text-foreground"
                    : "text-faint hover:text-muted-foreground"
                )}
              >
                <Icone
                  aria-hidden
                  className={cn("size-6", actif ? "opacity-100" : "opacity-80")}
                />
                <span className="font-heading text-[0.75rem] tracking-brand uppercase">
                  {item.label}
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
